import axios from '@/api/axios.config'
import api from '@/utils/global/api'

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'

type HeaderRecord = Record<string, string>

interface RequestHeadersOption {
  headers?: HeaderRecord
}

type JsonPrimitive = string | number | boolean | null
type JsonValue = JsonPrimitive | JsonObject | JsonArray
interface JsonObject {
  [key: string]: JsonValue
}
type JsonArray = Array<JsonValue>

type BodyInitLike = BodyInit | JsonObject | JsonArray | undefined

type ApiResponse<T> = T | string | Blob

function isBodyInit(value: unknown): value is BodyInit {
  return (
    value instanceof Blob ||
    value instanceof FormData ||
    value instanceof URLSearchParams ||
    value instanceof ReadableStream ||
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value as ArrayBufferView)
  )
}

function isPlainJson(value: unknown): value is JsonObject | JsonArray {
  if (!value || typeof value !== 'object') return false
  if (isBodyInit(value)) return false
  return true
}

const request =
  <T = unknown>(method: HttpMethod) =>
  async (
    endpoint: string,
    body?: BodyInitLike,
    reqHeaders: RequestHeadersOption = {},
  ): Promise<ApiResponse<T>> => {
    let headers: HeaderRecord = {
      'Content-Type': 'application/json',
      ...reqHeaders.headers,
    }

    if (method === 'GET' && body && typeof body === 'object') {
      headers = { ...headers, ...(body as HeaderRecord) }
      body = undefined
    }

    const commonHeadersUnknown = axios.defaults.headers.common as unknown
    if (
      typeof commonHeadersUnknown === 'object' &&
      commonHeadersUnknown !== null
    ) {
      const possibleAuth = (commonHeadersUnknown as Record<string, unknown>)[
        'Authorization'
      ]
      if (typeof possibleAuth === 'string' && possibleAuth.length > 0) {
        headers['Authorization'] = possibleAuth
      }
    }

    let preparedBody: BodyInit | undefined
    if (body !== undefined) {
      if (isPlainJson(body)) {
        preparedBody = JSON.stringify(body)
      } else if (isBodyInit(body)) {
        preparedBody = body
        // If sending non-JSON body, let browser set content-type when appropriate
        if (body instanceof FormData) {
          delete headers['Content-Type']
        }
      } else if (typeof body === 'string') {
        preparedBody = body
      }
    }

    const res = await fetch(`${api}${endpoint}`, {
      method,
      body: preparedBody,
      headers,
    })

    const contentType = res.headers.get('content-type') || ''

    if (contentType.includes('pdf')) return res.blob()
    if (!contentType.includes('json')) return res.text()

    const data = (await res.json()) as T & { message?: string }
    if (!res.ok) {
      throw data?.message || 'Error'
    }

    return data
  }

export const fetchClient = {
  del: request('DELETE'),
  get: request('GET'),
  post: request('POST'),
  patch: request('PATCH'),
}

export type { ApiResponse, RequestHeadersOption }
